import React, { useState, useEffect, useCallback } from 'react';
import { Container, Section } from 'components/shared';
import { getFieldValue } from '@sitecore-jss/sitecore-jss-react';
import styled from 'styled-components';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import { useSitecoreContext } from 'stores/sitecoreContext';
import templateIds from 'utils/templateIds';
import useFavorites from 'utils/hooks/useFavorites';
import {
  videoTranscriptFileUrl,
  videoTranscriptFileExtension,
} from 'utils/videoTranscriptFileUrl';
import breakpoints from 'utils/breakpoints';
import { isValidTitle } from 'utils/validateTitle';
import useNewColorSchemaOptions from 'utils/hooks/useNewColorSchemaOptions';
import { isValidUrl } from 'utils/validateUrlSrc';
import trackAnalyticsEvent from 'utils/trackAnalyticsEvent';

import loadable from '@loadable/component';
const DynamicWidthGallery = loadable(() =>
  import('components/components/organisms/DynamicWidthGallery'),
);
const Heading = loadable(() => import('components/components/atoms/Heading'));
const Copy = loadable(() => import('components/components/atoms/Copy'));

const StyledSection = styled(Section)`
  ${({ noPaddingTop }) =>
    noPaddingTop &&
    `
    padding-top: 0 !important;
  `}
`;

const StyledCopy = styled(Copy)`
  @media ${breakpoints.lg} {
    width: 55rem;
  }
`;

const filterCtas = (media) => {
  if (media && media.length > 0) {
    const ctas = [
      { key: 'tour', text: '360 Tours' },
      { key: 'image', text: 'Photos' },
      { key: 'video', text: 'Videos' },
    ];
    const filterCtas = ctas.filter((cta) => {
      const itemIndex = media.findIndex(
        (i) =>
          i.type === cta.key ||
          (cta.key === 'video' && ['external', 'internal'].includes(i.type)),
      );
      return itemIndex !== -1;
    });
    let sortCtas = [];
    const hasVideo = filterCtas.find((e) => e.key === 'video');
    if (hasVideo) sortCtas.push(hasVideo);
    const hasTour = filterCtas.find((e) => e.key === 'tour');
    if (hasTour) sortCtas.push(hasTour);
    const hasImage = filterCtas.find((e) => e.key === 'image');
    if (hasImage) sortCtas.push(hasImage);
    return sortCtas;
  }
  return [];
};

const getGallery = (media, filter) => {
  if (!filter) return media;
  if (filter === 'video') {
    return media.filter(
      (e) => e.type.includes('internal') || e.type.includes('external'),
    );
  }
  return media.filter((e) => e.type.includes(filter));
};

const DynamicWidthGallerySection = ({ fields }) => {
  const toCleanMedia = getFieldValue(fields, 'media') || [];
  const media = toCleanMedia.filter((item) => {
    if (item && item.type) {
      if (item.type === 'tour') {
        return (
          item.src &&
          item.src.includes('iframe') &&
          !/\.(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(item.src)
        );
      }
      if (item.type === 'image') {
        return item.src && isValidUrl(item.src);
      }
      if (['internal', 'external'].includes(item.type)) {
        return (
          item.src &&
          isValidUrl(item.src) &&
          !/\.(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(item.src)
        );
      }
    }
    return false;
  });
  const ctas = getFieldValue(fields, 'ctas');
  const showFavorites = getFieldValue(fields, 'showFavorites');
  const showShare = getFieldValue(fields, 'showShare');
  const availableDateMessage = getFieldValue(fields, 'availableDateMessage');
  const modelHome = getFieldValue(fields, 'modelHome');
  const title = getFieldValue(fields, 'title');
  const copy = getFieldValue(fields, 'copy');

  const { favoritesList, saveFavorite, deleteFavorite } = useFavorites();
  const {
    showNewSchemaColor,
    newSchemaTextColorHexaCode,
    newSchemaColorHexaCode,
  } = useNewColorSchemaOptions();

  const { templateId } = useSitecoreContext();

  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['sm', 'xs'].includes(breakpoint);

  const [filter, setFilter] = useState();
  const [position, setPosition] = useState(0);
  let filteredMedia = getGallery(media, filter);
  const filteredCtas = filterCtas(media);

  const handleFilterChange = useCallback(
    (value) => {
      switch (value) {
        case '360 Tours':
          if (filter !== 'tour')
            trackAnalyticsEvent({
              gtagEvent: 'VirtualTourFilterClick',
              mouseflowTag: 'VirtualTourFilterClick',
            });
          setFilter(filter !== 'tour' ? 'tour' : undefined);
          setPosition(0);
          break;
        case 'Videos':
          if (filter !== 'video')
            trackAnalyticsEvent({
              gtagEvent: 'GalleryVideosClick',
              mouseflowTag: 'GalleryVideosClick',
            });
          setFilter(filter !== 'video' ? 'video' : undefined);
          setPosition(0);
          break;
        case 'Photos':
          setFilter(filter !== 'image' ? 'image' : undefined);
          setPosition(0);
          break;
      }
    },
    [filter],
  );

  if (!media || !media.length) {
    return null;
  }

  const showHeading = ![
    templateIds.PlanPage,
    templateIds.QMIPage,
    templateIds.MpcPage,
  ].includes(templateId);

  const renderVideoDescription = () =>
    filteredMedia
      .filter((m) => m.type === 'internal' || m.type === 'external')
      .filter(
        (m) =>
          m.alt && m.videoAltTranscriptFile && m.videoAltTranscriptFile.link,
      )
      .map((m, i) => (
        <span
          key={i}
          className='richtext text-left text-md leading-7 font-graphie text-mattamy-blue'
        >
          {'Transcript: '}
          <a
            href={videoTranscriptFileUrl(m.videoAltTranscriptFile)}
            download={
              m.videoAltTranscriptFile.name +
              videoTranscriptFileExtension(m.videoAltTranscriptFile.extension)
            }
            className='underline'
            aria-label={'Download ' + m.alt + ' transcript'}
          >
            {m.alt}
          </a>
        </span>
      ));
  return (
    <StyledSection
      id='gallery'
      className='pt-5 lg:pt-0'
      noPaddingTop={!title || !showHeading}
      tabIndex={'-1'}
    >
      <Container className='flex flex-col items-center' noPadding>
        {isValidTitle(title) && showHeading && (
          <Heading
            as='h2'
            className={'text-center pb-2'}
            color={showNewSchemaColor ? 'black' : 'blue'}
          >
            {title}
          </Heading>
        )}
        {copy && showHeading && (
          <StyledCopy
            color={showNewSchemaColor ? 'black' : 'blue'}
            className='md:text-lg text-center pb-6 px-8'
          >
            {copy}
          </StyledCopy>
        )}

        <DynamicWidthGallery
          className='w-full'
          carousel={filteredMedia}
          ctas={ctas}
          showFavorites={showFavorites}
          showShare={showShare}
          availableDateMessage={availableDateMessage}
          modelHome={modelHome}
          handleFilterChange={handleFilterChange}
          filter={filter}
          favoritesList={favoritesList}
          saveFavorite={saveFavorite}
          deleteFavorite={deleteFavorite}
          showNewSchemaColor={showNewSchemaColor}
          newSchemaColorHexaCode={newSchemaColorHexaCode}
          newSchemaTextColorHexaCode={newSchemaTextColorHexaCode}
          filteredCtas={filteredCtas}
          position={position}
          setPosition={setPosition}
        />
        {filteredMedia && (
          <div className='w-full mt-2 flex flex-col'>
            {renderVideoDescription()}
          </div>
        )}
      </Container>
    </StyledSection>
  );
};

export default DynamicWidthGallerySection;
